import React, { useEffect, useRef } from "react";

/**
 * Google reCAPTCHA v2 ("I'm not a robot" checkbox + image challenge).
 *
 * Used only on the interpreter job-ACCEPT action to stop auto-accept bots. v2 renders a visible
 * widget the user must solve; the resulting token is handed back via onToken and sent with the
 * accept request (backend verifies it).
 *
 * The app already loads grecaptcha globally for invisible v3; grecaptcha.render() with an explicit
 * v2 sitekey is Google's supported way to run a v2 widget alongside a v3 `?render=` script, so we
 * reuse the loaded API and only fall back to injecting the base api.js if it isn't present yet.
 * Everything no-ops when REACT_APP_RECAPTCHA_V2_SITE_KEY is unset.
 */

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY;
const SCRIPT_ID = "recaptcha-v2-api";

let scriptPromise = null;
const ensureGrecaptcha = () => {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve) => {
    if (document.getElementById(SCRIPT_ID)) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return scriptPromise;
};

function RecaptchaV2({ onToken }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || widgetIdRef.current !== null || !containerRef.current) return;
      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
        });
      } catch (e) {
        // already rendered in this node, or a key/config error — leave it unsolved
        console.log("reCAPTCHA v2 render error", e);
      }
    };

    ensureGrecaptcha().then((ok) => {
      if (!ok || cancelled || !window.grecaptcha?.render) return;
      if (window.grecaptcha.ready) {
        window.grecaptcha.ready(render);
      } else {
        render();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [onToken]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} style={{ display: "flex", justifyContent: "center" }} />;
}

export default RecaptchaV2;
